import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { DpaFooterSections } from './DpaFooterSections';

import type { ReactElement } from 'react';

/**
 * Trust Center — DPA page (`/trust/dpa`). Phase 6 Pass 3 iter 249.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/dpa`: "DPA
 * template download; SCCs; UK IDTA addendum; Swiss FADP rider."
 *
 * **Iter-249 scope** (Lead Dev hat, locked): the contract package
 * is laid out here in full text. The signed PDF artefacts (the
 * DPA template, SCCs Module Two, UK IDTA addendum, Swiss FADP
 * rider) ship as `<a download>` links from this page WHEN BL-013
 * (EU privacy counsel review) resolves — counsel must sign off
 * before any document is binding. Until then, this page is the
 * authoritative public statement of what the contract package
 * contains; customers can request a working copy via
 * admin@syncanix.com and the contract is negotiated bilaterally.
 *
 * Avoiding the half-done failure mode (`.claude/rules/quality-bar
 * .md`): we do NOT ship "Download (coming soon)" buttons. The
 * page is complete in itself; PDFs land as an additive layer.
 *
 * **Marketing voice (Marketer hat, locked)**: concrete legal
 * citations (Article 28 / 32 / 33 / 44-49 GDPR; SCCs Module Two;
 * Commission Implementing Decision 2021/914) + concrete addendum
 * names + concrete jurisdictions for regional appendices.
 *
 * **i18n**: this is binding legal text; the English version
 * governs (see `<LegalTranslationNotice />`). Every user-facing
 * string resolves via `useT()` under the `dpa.*` namespace; GDPR
 * article numbers, the "SCCs" term, sub-processor names, dates,
 * entity names, emails, and URLs are preserved verbatim across
 * locales.
 *
 * **Three-layer contract preserved**: pure presentational. No
 * data fetch.
 */
export function Dpa(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('dpa.meta.title'),
    description: t('dpa.meta.description'),
    path: '/trust/dpa',
  });
  return (
    <LegalPage
      breadcrumb={t('dpa.backToTrust')}
      title={t('dpa.title')}
      intro={
        <>
          {t('dpa.intro.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('dpa.intro.after')}
        </>
      }
      lastUpdatedSlug="dpa"
    >
      <section aria-labelledby="dpa-package" className="mb-10">
        <h2
          id="dpa-package"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.package.heading')}
        </h2>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('dpa.package.dpa.term')}</strong> {t('dpa.package.dpa.body')}
          </li>
          <li>
            <strong>{t('dpa.package.sccs.term')}</strong> {t('dpa.package.sccs.body')}
          </li>
          <li>
            <strong>{t('dpa.package.idta.term')}</strong> {t('dpa.package.idta.body')}
          </li>
          <li>
            <strong>{t('dpa.package.swiss.term')}</strong> {t('dpa.package.swiss.body')}
          </li>
          <li>
            <strong>{t('dpa.package.regional.term')}</strong> {t('dpa.package.regional.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="dpa-scope" className="mb-10">
        <h2
          id="dpa-scope"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.scope.heading')}
        </h2>
        <p className="mb-3">{t('dpa.scope.intro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('dpa.scope.instructions.term')}</strong> {t('dpa.scope.instructions.body')}
          </li>
          <li>
            <strong>{t('dpa.scope.confidentiality.term')}</strong>{' '}
            {t('dpa.scope.confidentiality.body')}
          </li>
          <li>
            <strong>{t('dpa.scope.security.term')}</strong> {t('dpa.scope.security.before')}{' '}
            <Link className={INLINE_LINK} to="/trust/security">
              {t('dpa.scope.security.link')}
            </Link>
            {t('dpa.scope.security.after')}
          </li>
          <li>
            <strong>{t('dpa.scope.subprocessor.term')}</strong> {t('dpa.scope.subprocessor.before')}{' '}
            <Link className={INLINE_LINK} to="/trust/subprocessors">
              {t('dpa.scope.subprocessor.link')}
            </Link>
            {t('dpa.scope.subprocessor.after')}
          </li>
          <li>
            <strong>{t('dpa.scope.rights.term')}</strong> {t('dpa.scope.rights.body')}
          </li>
          <li>
            <strong>{t('dpa.scope.breach.term')}</strong> {t('dpa.scope.breach.body')}
          </li>
          <li>
            <strong>{t('dpa.scope.audit.term')}</strong> {t('dpa.scope.audit.body')}
          </li>
          <li>
            <strong>{t('dpa.scope.deletion.term')}</strong> {t('dpa.scope.deletion.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="dpa-sccs" className="mb-10">
        <h2
          id="dpa-sccs"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.sccs.heading')}
        </h2>
        <p>
          {t('dpa.sccs.before')}{' '}
          <Link className={INLINE_LINK} to="/trust/subprocessors">
            {t('dpa.sccs.link')}
          </Link>
          {t('dpa.sccs.after')}
        </p>
      </section>

      <section aria-labelledby="dpa-uk" className="mb-10">
        <h2
          id="dpa-uk"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.uk.heading')}
        </h2>
        <p>{t('dpa.uk.body')}</p>
      </section>

      <section aria-labelledby="dpa-swiss" className="mb-10">
        <h2
          id="dpa-swiss"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.swiss.heading')}
        </h2>
        <p>{t('dpa.swiss.body')}</p>
      </section>

      <section aria-labelledby="dpa-regional" className="mb-10">
        <h2
          id="dpa-regional"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.regional.heading')}
        </h2>
        <p className="mb-3">{t('dpa.regional.intro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('dpa.regional.uae.term')}</strong> {t('dpa.regional.uae.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.saudi.term')}</strong> {t('dpa.regional.saudi.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.israel.term')}</strong> {t('dpa.regional.israel.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.egypt.term')}</strong> {t('dpa.regional.egypt.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.qatar.term')}</strong> {t('dpa.regional.qatar.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.bahrain.term')}</strong> {t('dpa.regional.bahrain.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.oman.term')}</strong> {t('dpa.regional.oman.body')}
          </li>
          <li>
            <strong>{t('dpa.regional.jordan.term')}</strong> {t('dpa.regional.jordan.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="dpa-counsel" className="mb-10">
        <h2
          id="dpa-counsel"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.counsel.heading')}
        </h2>
        <p>
          {t('dpa.counsel.before')} <code>docs/legal/counsel-sign-off.md</code>{' '}
          {t('dpa.counsel.middle')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          {t('dpa.counsel.after')}
        </p>
      </section>

      <section aria-labelledby="dpa-request" className="mb-10">
        <h2
          id="dpa-request"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('dpa.request.heading')}
        </h2>
        <ol className="list-decimal space-y-2 ps-6">
          <li>
            {t('dpa.request.step1.before')}{' '}
            <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
              admin@syncanix.com
            </a>{' '}
            {t('dpa.request.step1.after')}
          </li>
          <li>{t('dpa.request.step2')}</li>
          <li>{t('dpa.request.step3')}</li>
          <li>
            {t('dpa.request.step4.before')} <code>.pdf</code> {t('dpa.request.step4.after')}
          </li>
        </ol>
      </section>

      <DpaFooterSections />
    </LegalPage>
  );
}
